import React, { useState } from "react";
import { Box, Typography, Button, Collapse } from "@mui/material";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import casque from "../../assets/casque.png";
import velo from "../../assets/velo-elec.png";

const RotatingIcon = styled(ArrowCircleRightIcon)(({ open }) => ({
  transform: open ? "rotate(-90deg)" : "rotate(0deg)",
  transition: "transform 0.3s ease",
}));

const EquipementSection = () => {
  const { t } = useTranslation("garanties/EquipementSection");
  const [showVeloList, setShowVeloList] = useState(false);
  const [showEquipementList, setShowEquipementList] = useState(false);

  const typesVelo = [
    t("typesVelo.bike"),
    t("typesVelo.ebike"),
    t("typesVelo.mtBike"),
    t("typesVelo.eMtBike"),
    t("typesVelo.skateboard"),
    t("typesVelo.eSkateboard"),
    t("typesVelo.scooter"),
    t("typesVelo.eScooter"),
    t("typesVelo.gyro"),
    t("typesVelo.roller"),
    t("typesVelo.eRoller"),
    t("typesVelo.hoverboard"),
    t("typesVelo.miniScooter"),
    t("typesVelo.unicycle"),
    t("typesVelo.tricycle"),
    t("typesVelo.eTricycle"),
    t("typesVelo.quadricycle"),
    t("typesVelo.eQuadricycle"),
  ];

  const typesEquipement = [
    t("typesEquipement.protection"),
    t("typesEquipement.speedometer"),
    t("typesEquipement.lighting"),
    t("typesEquipement.lock"),
  ];

  const renderBulletList = (items) => {
    const mid = Math.ceil(items.length / 2);
    const left = items.slice(0, mid);
    const right = items.slice(mid);

    return (
      <Box sx={{ display: "flex", gap: 4, mt: 1 }}>
        {[left, right].map((col, i) => (
          <Box key={i} component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
            {col.map((item, idx) => (
              <Box
                key={idx}
                component="li"
                sx={{ color: "#333", fontSize: "1rem", mb: 1, display: "flex", alignItems: "start" }}
              >
                <Box component="span" sx={{ mr: 1 }}>•</Box> {item}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", px: 2, mt: 10 }}>
      <Box sx={{ maxWidth: "1200px", width: "100%", fontFamily: "'Poppins', sans-serif" }}>
        {/* Titre principal */}
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: "2.2rem", md: "3.5rem" },
            lineHeight: 1.2,
            textAlign: "center",
            color: "#000",
            mb: 6,
          }}
        >
          {t("title.part1")}{" "}
          <Box component="span" sx={{ color: "#FFC22F" }}>
            {t("title.highlight")}
          </Box>
        </Typography>

        {/* Paragraphe explicatif (exactement comme ton FR) */}
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "1.1rem",
            maxWidth: "700px",
            mx: "auto",
            color: "#444",
            mb: 4,
          }}
        >
          {t("paragraph.1")} <Box component="span" fontWeight="bold">{t("paragraph.boldVehicles")}</Box> {t("paragraph.2")}{" "}
          <Box component="span" fontWeight="bold">{t("paragraph.boldAccessories")}</Box> {t("paragraph.3")}{" "}
          <Box component="span" fontWeight="bold">{t("paragraph.boldMobility")}</Box>.{" "}
          {t("paragraph.4")} <Box component="span" fontWeight="bold">{t("paragraph.boldDamage")}</Box>,{" "}
          <Box component="span" fontWeight="bold">{t("paragraph.boldTheft")}</Box> {t("paragraph.and")}{" "}
          <Box component="span" fontWeight="bold">{t("paragraph.boldBreakdowns")}</Box>.{" "}
          {t("paragraph.5")}
        </Typography>

        {/* Deux encadrés */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: "stretch",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          {/* Bloc Vélos */}
          <Box sx={{ width: { xs: "100%", sm: "100%", md: "48%" }, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: "20px",
                padding: 4,
                textAlign: "center",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: "2rem", color: "#000", mb: 3 }}>
                {t("cards.mobility")}
              </Typography>

              <Box component="img" src={velo} alt="Vélos" sx={{ width: "100%", height: "260px", objectFit: "contain", mb: 3 }} />

              <Button
                onClick={() => setShowVeloList(!showVeloList)}
                endIcon={<RotatingIcon open={showVeloList} />}
                sx={{
                  color: "#000",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  "&:hover": { textDecoration: "underline", background: "transparent" },
                }}
              >
                {t("buttons.showAllVehicles")}
              </Button>

              <Collapse in={showVeloList} unmountOnExit>
                <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: "16px", p: 3, textAlign: "left", width: "100%" }}>
                  {renderBulletList(typesVelo)}
                </Box>
              </Collapse>
            </Box>
          </Box>

          {/* Bloc Accessoires */}
          <Box sx={{ width: { xs: "100%", sm: "100%", md: "48%" }, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: "20px",
                padding: 4,
                textAlign: "center",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: "2rem", color: "#000", mb: 3 }}>
                {t("cards.equipment")}
              </Typography>

              <Box component="img" src={casque} alt="Accessoires" sx={{ width: "100%", height: "260px", objectFit: "contain", mb: 3 }} />

              <Button
                onClick={() => setShowEquipementList(!showEquipementList)}
                endIcon={<RotatingIcon open={showEquipementList} />}
                sx={{
                  color: "#000",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  "&:hover": { textDecoration: "underline", background: "transparent" },
                }}
              >
                {t("buttons.showAllEquipment")}
              </Button>

              <Collapse in={showEquipementList} unmountOnExit>
                <Box sx={{ backgroundColor: "#f5f5f5", borderRadius: "16px", p: 3, textAlign: "left", width: "100%" }}>
                  {renderBulletList(typesEquipement)}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EquipementSection;